import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import Supercluster from 'supercluster';
import L from 'leaflet';
import { useApi } from '../apiHelpers';
import {
  MAINTENANCE_LOCATION,
  stringToColor,
  getDronePosition,
  isLive,
  createDroneIcon,
  createClusterIcon,
} from './mapHelpers';
import 'leaflet/dist/leaflet.css';
import { buttonClass } from '../components/ui/Button';
import { FieldRow, Field } from '../components/ui/Field';
import { SegTrack } from '../components/ui/SegTrack';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

const POLL_INTERVAL = 30000; // 30 seconds

// Date filter presets
const DATE_PRESETS = {
  PAST_WEEK: 'past_week',
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  CUSTOM: 'custom',
  LAST_SEEN_ON: 'last_seen_on', // Find drones whose final ping was on a specific date
};

// Get date string in YYYY-MM-DD format
const formatDateForInput = (date) => {
  return date.toISOString().split('T')[0];
};

// Get date X days ago
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

// Component to auto-fit map bounds to markers (only on initial load)
function FitBounds({ positions, hasInitialized }) {
  const map = useMap();

  useEffect(() => {
    if (hasInitialized.current || positions.length === 0) return;

    if (positions.length === 1) {
      map.setView(positions[0], 12);
    } else {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
    hasInitialized.current = true;
  }, [map, positions, hasInitialized]);

  return null;
}

// Component that handles clustering and rendering markers based on map state
function ClusterMarkers({ drones, supercluster, onDroneClick }) {
  const map = useMap();
  const [clusters, setClusters] = useState([]);

  // Update clusters when map moves or zooms
  const updateClusters = useCallback(() => {
    if (!supercluster || drones.length === 0) {
      setClusters([]);
      return;
    }

    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];
    const currentZoom = Math.floor(map.getZoom());
    const newClusters = supercluster.getClusters(bbox, currentZoom);
    setClusters(newClusters);
  }, [map, supercluster, drones.length]);

  // Listen to map events
  useMapEvents({
    moveend: updateClusters,
    zoomend: updateClusters,
  });

  // Initial update
  useEffect(() => {
    updateClusters();
  }, [updateClusters]);

  return (
    <>
      {clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const position = [latitude, longitude];

        // It's a cluster
        if (cluster.properties.cluster) {
          const { cluster_id, point_count, dominantColor, hasLive } = cluster.properties;

          return (
            <Marker
              key={`cluster-${cluster_id}`}
              position={position}
              icon={createClusterIcon(point_count, dominantColor, hasLive)}
              eventHandlers={{
                click: () => {
                  // Zoom into cluster
                  const expansionZoom = Math.min(
                    supercluster.getClusterExpansionZoom(cluster_id),
                    18
                  );
                  map.setView(position, expansionZoom, { animate: true });
                },
              }}
            />
          );
        }

        // It's a single point
        const drone = cluster.properties;
        const color = stringToColor(drone.last_seen_server);
        const isPinging = isLive(drone);

        return (
          <Marker
            key={drone.serialnumber}
            position={position}
            icon={createDroneIcon(color, isPinging)}
          >
            <Popup>
              <DronePopup drone={drone} color={color} onViewDrone={onDroneClick} />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

// Popup content component
function DronePopup({ drone, color, onViewDrone }) {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="min-w-[150px]">
      <p className="text-primary font-bold text-center border-b border-primary pb-1 mb-2">
        {drone.serialnumber}
      </p>
      <p className="text-xs text-on-surface-variant flex items-center justify-between gap-2">
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          {drone.last_seen_server || 'Unknown'}
        </span>
        <span>{formatTime(drone.last_seen_time)}</span>
      </p>
      {drone.project_assignment && (
        <p className="text-xs text-primary mt-1">Project: {drone.project_assignment}</p>
      )}
      <div className="text-center">
        <button
          onClick={() => onViewDrone(drone.serialnumber)}
          className={`${buttonClass('tonal', 'sm')} mt-2`}
        >
          View Drone
        </button>
      </div>
    </div>
  );
}

function DroneMap() {
  const history = useHistory();
  const api = useApi();
  const [drones, setDrones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitializedBounds = useRef(false);
  const isMountedRef = useRef(true);

  // Date filter state - default to "past week" (current behavior)
  const [datePreset, setDatePreset] = useState(DATE_PRESETS.PAST_WEEK);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lastSeenOnDate, setLastSeenOnDate] = useState(''); // For finding lost drones
  const [showFilters, setShowFilters] = useState(false);

  // Build query params based on filter state
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    if (datePreset === DATE_PRESETS.PAST_WEEK) {
      return '';
    }

    if (datePreset === DATE_PRESETS.TODAY) {
      const today = formatDateForInput(new Date());
      params.set('startDate', today);
      params.set('endDate', today);
    } else if (datePreset === DATE_PRESETS.YESTERDAY) {
      const yesterday = formatDateForInput(daysAgo(1));
      params.set('startDate', yesterday);
      params.set('endDate', yesterday);
    } else if (datePreset === DATE_PRESETS.CUSTOM) {
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
    } else if (datePreset === DATE_PRESETS.LAST_SEEN_ON) {
      if (lastSeenOnDate) params.set('lastSeenOnDate', lastSeenOnDate);
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }, [datePreset, startDate, endDate, lastSeenOnDate]);

  // Fetch drones with current filters
  const fetchDrones = useCallback(async () => {
    try {
      const queryParams = buildQueryParams();
      const data = await api.get(`/fleet/recent-locations${queryParams}`);

      if (!isMountedRef.current) return;

      const validDrones = data.filter((drone) => {
        if (drone.last_seen_server?.startsWith('Maintenance by')) {
          return true;
        }
        if (!drone.last_seen_location) return false;
        const parts = drone.last_seen_location.split(',');
        if (parts.length !== 2) return false;
        const lat = parseFloat(parts[0].trim());
        const lon = parseFloat(parts[1].trim());
        return !isNaN(lat) && !isNaN(lon);
      });

      setDrones(validDrones);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch drone locations:', err);
      if (isMountedRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildQueryParams]);

  // Initial fetch and polling
  useEffect(() => {
    isMountedRef.current = true;

    fetchDrones();
    const interval = setInterval(fetchDrones, POLL_INTERVAL);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchDrones]);

  // Create GeoJSON features for Supercluster
  const geoJsonPoints = useMemo(() => {
    return drones.map((drone) => {
      const pos = getDronePosition(drone);
      return {
        type: 'Feature',
        properties: {
          ...drone,
          color: stringToColor(drone.last_seen_server),
          isLive: isLive(drone),
        },
        geometry: {
          type: 'Point',
          coordinates: [pos[1], pos[0]], // GeoJSON is [lon, lat]
        },
      };
    });
  }, [drones]);

  // Create Supercluster instance
  const supercluster = useMemo(() => {
    if (geoJsonPoints.length === 0) return null;

    const index = new Supercluster({
      radius: 40, // Cluster radius in pixels
      maxZoom: 16, // Max zoom to cluster points
      minZoom: 0,
      // Custom reduce function to aggregate cluster properties
      map: (props) => ({
        colors: { [props.color]: 1 },
        hasLive: props.isLive ? 1 : 0,
      }),
      reduce: (accumulated, props) => {
        // Accumulate colors
        for (const [color, count] of Object.entries(props.colors)) {
          accumulated.colors[color] = (accumulated.colors[color] || 0) + count;
        }
        // Track if any drone is live
        accumulated.hasLive = accumulated.hasLive || props.hasLive;
      },
    });

    index.load(geoJsonPoints);

    // Post-process to calculate dominant color for each cluster
    // This is done when getting clusters, not here
    return index;
  }, [geoJsonPoints]);

  // Wrap supercluster to add dominant color calculation
  const wrappedSupercluster = useMemo(() => {
    if (!supercluster) return null;

    return {
      getClusters: (bbox, zoom) => {
        const clusters = supercluster.getClusters(bbox, zoom);
        return clusters.map((cluster) => {
          if (cluster.properties.cluster) {
            // Find dominant color from accumulated colors
            const colors = cluster.properties.colors || {};
            let dominantColor = '#888888';
            let maxCount = 0;
            for (const [color, count] of Object.entries(colors)) {
              if (count > maxCount) {
                maxCount = count;
                dominantColor = color;
              }
            }
            cluster.properties.dominantColor = dominantColor;
            cluster.properties.hasLive = !!cluster.properties.hasLive;
          }
          return cluster;
        });
      },
      getClusterExpansionZoom: (clusterId) => supercluster.getClusterExpansionZoom(clusterId),
    };
  }, [supercluster]);

  // Extract positions for bounds fitting
  const positions = useMemo(() => {
    return drones.map((drone) => getDronePosition(drone));
  }, [drones]);

  const handleViewDrone = useCallback((serialnumber) => {
    history.push(`/drone/${serialnumber}`);
  }, [history]);

  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  return (
    <div className="text-white relative" style={{ height: 'calc(100dvh - var(--nav-h, 0px))' }}>
      {/* CSS for ping animation and dark popup styling */}
      <style>{`
        @keyframes ping-ring {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
          100% {
            transform: translate(-50%, -50%) scale(4);
            opacity: 0;
          }
        }

        .leaflet-popup-content-wrapper {
          background-color: #191919;
          color: var(--on-surface);
          border-radius: 2px;
        }
        .leaflet-popup-tip {
          background-color: #191919;
        }
        .leaflet-popup-close-button {
          color: var(--on-surface-variant) !important;
        }
        .leaflet-popup-close-button:hover {
          color: #ffcc33 !important;
        }
      `}</style>

      {/* Title overlay - centered */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-black/70 px-4 py-2 rounded-lg backdrop-blur-sm">
        <h1 className="text-xl font-bold">
          {datePreset === DATE_PRESETS.LAST_SEEN_ON && lastSeenOnDate
            ? `Drones Last Seen on ${lastSeenOnDate}`
            : `Firefly Drones ${datePreset === DATE_PRESETS.PAST_WEEK ? 'Seen in the Past Week' :
              datePreset === DATE_PRESETS.TODAY ? 'Seen Today' :
              datePreset === DATE_PRESETS.YESTERDAY ? 'Seen Yesterday' :
              datePreset === DATE_PRESETS.CUSTOM && startDate && endDate ? `Seen ${startDate} to ${endDate}` :
              datePreset === DATE_PRESETS.CUSTOM && startDate ? `Seen Since ${startDate}` :
              datePreset === DATE_PRESETS.CUSTOM && endDate ? `Seen Until ${endDate}` : 'Seen'}`}: {drones.length}
        </h1>
      </div>

      {/* Date Filter Panel */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col items-end">
        <button
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
          className={buttonClass(showFilters ? 'filled' : 'tonal', 'md')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filter
        </button>

        <Reveal show={showFilters}>
          <div className="mt-2 w-[340px] max-w-[calc(100vw-2rem)] rounded-panel bg-surface-container-low p-4 shadow-overlay">
            <h3 className="font-semibold mb-2 text-primary">Date Filter</h3>
            <p className="text-xs text-on-surface-variant mb-3">All times are in UTC</p>

            {/* Preset buttons */}
            <SegTrack
              label="Date filter"
              size="sm"
              fill
              className="mb-4"
              value={datePreset}
              onChange={setDatePreset}
              options={[
                { key: DATE_PRESETS.PAST_WEEK, label: 'Past Week' },
                { key: DATE_PRESETS.TODAY, label: 'Today' },
                { key: DATE_PRESETS.YESTERDAY, label: 'Yesterday' },
                { key: DATE_PRESETS.CUSTOM, label: 'Date Range' },
              ]}
            />

            {/* Custom date range inputs */}
            <Reveal show={datePreset === DATE_PRESETS.CUSTOM}>
              <div className="space-y-3 mb-4">
                <FieldRow label="Start Date">
                  <Field
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FieldRow>
                <FieldRow label="End Date">
                  <Field
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </FieldRow>
              </div>
            </Reveal>

            {/* Find Lost Drones section */}
            <div className="mt-5">
              <h4 className="font-semibold mb-2 text-orange-400 text-sm">Find Lost Drones</h4>
              <p className="text-xs text-on-surface-variant mb-3">
                Show drones whose last connection was on a specific date and reported GPS that day.
                Useful for finding drones that didn't check in at the shop.
              </p>
              <button
                type="button"
                aria-pressed={datePreset === DATE_PRESETS.LAST_SEEN_ON}
                onClick={() => setDatePreset(DATE_PRESETS.LAST_SEEN_ON)}
                className={`mb-2 w-full justify-center ${buttonClass(datePreset === DATE_PRESETS.LAST_SEEN_ON ? 'warn' : 'tonal', 'md')}`}
              >
                Last Seen On Date
              </button>
              <Reveal show={datePreset === DATE_PRESETS.LAST_SEEN_ON}>
                <FieldRow label="Select Date">
                  <Field
                    type="date"
                    value={lastSeenOnDate}
                    onChange={(e) => setLastSeenOnDate(e.target.value)}
                  />
                </FieldRow>
              </Reveal>
            </div>
          </div>
        </Reveal>
      </div>

      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        attributionControl={false}
        maxZoom={20}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxNativeZoom={18}
          maxZoom={25}
        />

        <FitBounds positions={positions} hasInitialized={hasInitializedBounds} />

        {wrappedSupercluster && (
          <ClusterMarkers
            drones={drones}
            supercluster={wrappedSupercluster}
            onDroneClick={handleViewDrone}
          />
        )}
      </MapContainer>

      {isLoading && (
        <div className="absolute inset-0 z-[1000] pointer-events-none">
          <LogoSpinner label="Loading drone locations" className="h-full" />
        </div>
      )}

      {!isLoading && drones.length === 0 && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1000]">
          <FadeIn>
            <EmptyState variant="panel">
              No drones with GPS data found for the selected date range
            </EmptyState>
          </FadeIn>
        </div>
      )}
    </div>
  );
}

export default DroneMap;
